// Shared authentication helper utilities
// Consolidates common token persistence, expiry checks, and account building logic
// Used by auth0.ts and webapp-pkce-auth.ts

import type { StoredExtensionAuth } from "../types/api";

export interface PersistAdapter {
  load(): StoredExtensionAuth | undefined;
  save(auth: StoredExtensionAuth): void;
  clear(): void;
}

function createStorageAdapter(storageKey: string, storageType: 'localStorage' | 'sessionStorage'): PersistAdapter {
  return {
    load() {
      try {
        const raw = window?.[storageType]?.getItem(storageKey);
        return raw ? (JSON.parse(raw) as StoredExtensionAuth) : undefined;
      } catch {
        return undefined;
      }
    },
    save(auth) {
      try {
        window?.[storageType]?.setItem(storageKey, JSON.stringify(auth));
      } catch {
        /* ignore */
      }
    },
    clear() {
      try {
        window?.[storageType]?.removeItem(storageKey);
      } catch {
        /* ignore */
      }
    },
  };
}

export function createLocalStorageAdapter(storageKey: string): PersistAdapter {
  return createStorageAdapter(storageKey, 'localStorage');
}

export function createSessionStorageAdapter(storageKey: string): PersistAdapter {
  return createStorageAdapter(storageKey, 'sessionStorage');
}

export function isAuthExpired(expiresAt?: number, leewayMs = 60_000): boolean {
  if (!expiresAt) return true;
  return Date.now() + leewayMs >= expiresAt;
}

export function buildAccountFromClaims(claims: Record<string, unknown> | undefined): StoredExtensionAuth["account"] {
  if (!claims || typeof claims !== "object") return undefined;
  const c = claims as Record<string, unknown>;
  const account: NonNullable<StoredExtensionAuth["account"]> = {};
  const asString = (v: unknown) => (typeof v === "string" && v.trim() ? v.trim() : undefined);
  const userId = asString(c.userId || c.sub);
  if (userId) account.userId = userId;
  const email = asString(c.email);
  if (email) account.email = email;
  const name = asString(c.name || c.nickname);
  if (name) account.name = name;
  const subject = asString(c.sub);
  if (subject) account.subject = subject;
  const isSuperUser = typeof c.isSuperUser === "boolean" ? c.isSuperUser : typeof c["https://lotlingo.com/claims/is_superuser"] === "boolean" ? (c["https://lotlingo.com/claims/is_superuser"] as boolean) : undefined;
  if (typeof isSuperUser === "boolean") account.isSuperUser = isSuperUser;
  return Object.keys(account).length ? account : undefined;
}

export interface TokenExchangeResponse {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number; // seconds
  refreshExpiresIn?: number; // seconds
  scopes?: string[] | string;
  idToken?: string;
  provider?: string;
}

export function mapTokenExchange(
  resp: TokenExchangeResponse,
  audience?: string,
  claims?: Record<string, unknown>
): StoredExtensionAuth {
  const now = Date.now();
  const scopesArray = Array.isArray(resp.scopes)
    ? resp.scopes
    : typeof resp.scopes === "string"
    ? resp.scopes.split(/\s+/).filter(Boolean)
    : [];
  return {
    provider: resp.provider || "auth0",
    tokenType: "Bearer",
    accessToken: resp.accessToken,
    refreshToken: resp.refreshToken,
    scopes: scopesArray,
    expiresAt: now + resp.expiresIn * 1000,
    refreshExpiresAt: resp.refreshExpiresIn ? now + resp.refreshExpiresIn * 1000 : undefined,
    audience,
    idToken: resp.idToken,
    account: buildAccountFromClaims(claims),
  };
}
